import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function denverTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      timeZone: "America/Denver",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDenverDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(dt);
}

export default function CheckinAttendee() {
  const { token } = useParams();
  const [ticket, setTicket] = useState(null); // null=loading, false=not found
  const [ballot, setBallot] = useState(null);
  const ballotRef = useRef(null);
  ballotRef.current = ballot;

  // Poll ticket status (event/tier/checked_in_at)
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/ticket-status/${encodeURIComponent(token)}`
        );
        if (!cancelled) setTicket(data?.found ? data : false);
      } catch {
        if (!cancelled) setTicket(false);
      }
    };
    refresh();
    const id = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  // Poll ballot state — every 3s once we know the ticket is real
  useEffect(() => {
    if (!ticket || ticket === false) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/ballot-state/${encodeURIComponent(token)}`
        );
        if (!cancelled) setBallot(data);
      } catch {
        /* keep last ballot state */
      }
    };
    refresh();
    const id = setInterval(refresh, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [ticket, token]);

  return (
    <>
      <Seo
        title="Your ticket — The Next Stage"
        description="Live status for your ticket."
        path={`/checkin/${token}`}
      />
      <section
        data-testid="checkin-attendee-page"
        className="max-w-xl mx-auto px-6 py-16 text-white text-center"
      >
        <div className="text-xs tracking-[0.25em] opacity-60 mb-3">
          THE NEXT STAGE
        </div>

        {ticket === null && (
          <div data-testid="checkin-attendee-loading" className="opacity-80">
            Checking your ticket…
          </div>
        )}

        {ticket === false && (
          <div data-testid="checkin-attendee-invalid">
            <h1 className="text-3xl sm:text-4xl font-semibold mb-4">
              This ticket isn't valid
            </h1>
            <p className="opacity-80 leading-relaxed mb-8">
              We couldn't find this ticket. If you've just purchased, please
              refresh in a moment — or come find a staff member.
            </p>
            <Link to="/" className="underline opacity-70">
              Back to home
            </Link>
          </div>
        )}

        {ticket && ticket !== false && (
          <AttendeeStatus ticket={ticket} ballot={ballot} token={token} />
        )}
      </section>
    </>
  );
}

function AttendeeStatus({ ticket, ballot, token }) {
  const status = ticket.status;
  const checkedIn = Boolean(ticket.checked_in_at);

  if (status === "void") {
    return (
      <div data-testid="checkin-attendee-void">
        <h1 className="text-3xl sm:text-4xl font-semibold mb-4">
          This ticket isn't valid
        </h1>
        <p className="opacity-80 leading-relaxed">
          Please find a staff member at the door.
        </p>
      </div>
    );
  }

  if (!checkedIn) {
    return (
      <div data-testid="checkin-attendee-not-yet">
        <h1 className="text-3xl sm:text-5xl font-semibold mb-4">Welcome.</h1>
        <p className="opacity-80 leading-relaxed mb-8">
          Please have a staff member scan your ticket at the door to check in.
        </p>
        <EventCard ticket={ticket} />
      </div>
    );
  }

  return (
    <div data-testid="checkin-attendee-checked-in">
      <h1 className="text-2xl sm:text-3xl font-semibold mb-2">
        You're checked in <span className="text-emerald-400">✓</span>
      </h1>
      <p className="opacity-70 leading-relaxed mb-6 text-sm">
        at {denverTime(ticket.checked_in_at)} Mountain Time
      </p>
      <EventCard ticket={ticket} compact />

      <Ballot ballot={ballot} token={token} />
    </div>
  );
}

function EventCard({ ticket, compact }) {
  if (!ticket?.event) return null;
  return (
    <div
      className={`border border-white/15 px-6 py-4 inline-block text-left ${
        compact ? "mt-2 mb-8" : "mt-2"
      }`}
    >
      <div className="text-[11px] tracking-[0.25em] uppercase opacity-60 mb-1">
        {ticket.tier_name}
      </div>
      <div className="text-lg font-semibold">{ticket.event.name}</div>
      <div className="opacity-70 text-sm">
        {formatDenverDate(ticket.event.event_date)} — {ticket.event.venue}
      </div>
    </div>
  );
}

// ---------- Ballot ----------
function Ballot({ ballot, token }) {
  // No ballot data yet → show light placeholder
  if (!ballot) {
    return (
      <p
        className="opacity-60 text-sm mt-10 leading-relaxed"
        data-testid="checkin-attendee-voting-placeholder"
      >
        Scoring will appear here once the show begins.
      </p>
    );
  }
  if (!ballot.can_submit) {
    return (
      <div
        className="mt-10 opacity-70 text-sm leading-relaxed"
        data-testid="ballot-waiting"
      >
        Waiting for the next speaker.
      </div>
    );
  }
  return (
    <BallotForm
      key={ballot.open_speaker.id /* reset form when speaker changes */}
      open={ballot.open_speaker}
      myScore={ballot.my_score}
      token={token}
    />
  );
}

const CATS = [
  { key: "content", label: "Content" },
  { key: "delivery", label: "Delivery" },
  { key: "impact", label: "Impact" },
];

function BallotForm({ open, myScore, token }) {
  const [values, setValues] = useState(
    myScore || { content: 0, delivery: 0, impact: 0 }
  );
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [error, setError] = useState("");

  // When the server's myScore changes (e.g. from a poll), trust it
  useEffect(() => {
    if (myScore) {
      setValues({
        content: myScore.content,
        delivery: myScore.delivery,
        impact: myScore.impact,
      });
    }
  }, [myScore?.content, myScore?.delivery, myScore?.impact]);

  const allPicked = CATS.every((c) => values[c.key] >= 1 && values[c.key] <= 5);

  const submit = async () => {
    if (!allPicked || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/public/ballot`, {
        token,
        speaker_id: open.id,
        ...values,
      });
      setSubmittedAt(new Date());
      if (data?.my_score) {
        setValues({
          content: data.my_score.content,
          delivery: data.my_score.delivery,
          impact: data.my_score.impact,
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Couldn't submit. Try once more in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-10 text-left border border-white/15 px-5 py-5"
      data-testid="ballot-form"
    >
      <div className="text-[11px] tracking-[0.25em] uppercase opacity-60 mb-1">
        Now Scoring · Slot #{(open.performance_order || 0) + 1}
      </div>
      <div className="text-xl font-semibold" data-testid="ballot-speaker-name">
        {open.display_name}
      </div>
      {open.talk_title && (
        <div className="opacity-70 text-sm">{open.talk_title}</div>
      )}

      <div className="mt-6 space-y-5">
        {CATS.map((c) => (
          <Row
            key={c.key}
            label={c.label}
            value={values[c.key]}
            onPick={(v) => setValues((s) => ({ ...s, [c.key]: v }))}
            testid={`ballot-${c.key}`}
          />
        ))}
      </div>

      <button
        data-testid="ballot-submit-btn"
        onClick={submit}
        disabled={!allPicked || submitting}
        className="mt-6 w-full py-3 font-semibold tracking-wider text-sm transition"
        style={{
          background: allPicked ? "#E5B25D" : "rgba(255,255,255,0.08)",
          color: allPicked ? "#0A0F1F" : "rgba(255,255,255,0.5)",
          cursor: allPicked && !submitting ? "pointer" : "not-allowed",
        }}
      >
        {myScore
          ? submitting
            ? "UPDATING…"
            : "UPDATE MY SCORE"
          : submitting
          ? "SUBMITTING…"
          : "SUBMIT MY SCORE"}
      </button>

      {submittedAt && (
        <div
          className="mt-3 text-sm text-emerald-400"
          data-testid="ballot-confirmed"
        >
          Locked in. You can edit until the speaker's window closes.
        </div>
      )}
      {!submittedAt && myScore && (
        <div
          className="mt-3 text-xs opacity-60"
          data-testid="ballot-prior-submission"
        >
          You've already scored this speaker — you can update until scoring closes.
        </div>
      )}
      {error && (
        <div className="mt-3 text-sm text-red-400" data-testid="ballot-error">
          {error}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, onPick, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] opacity-70 mb-2">
        {label}
      </div>
      <div className="grid grid-cols-5 gap-2" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            data-testid={`${testid}-${n}`}
            onClick={() => onPick(n)}
            className="py-3 text-base font-semibold transition border"
            style={{
              background: value === n ? "#E5B25D" : "transparent",
              color: value === n ? "#0A0F1F" : "#fff",
              borderColor: value === n ? "#E5B25D" : "rgba(255,255,255,0.2)",
            }}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
